import { type JSX, useRef } from "react";
import { useDialogKeyboard } from "../../../components/ui/useDialogKeyboard";
import { PalimpsestView } from "./PalimpsestView";

export type PalimpsestModalProps = {
  /** modal が開いているか。false のときは何も描画しない。 */
  open: boolean;
  /** 閉じるための統合コールバック (背景 click / Esc / Header 閉じる 共通)。 */
  onClose: () => void;
  scenarioId: string;
  currentWorldlineId: string | null;
  onForkSuccess?: (newWorldlineId: string) => void;
};

/**
 * worldline-palimpsest (Phase 3-1, Task 5.8) palimpsest modal shell。
 *
 * 既存 BackscrollView / WorldlineTreeView の modal パターン (z-50 固定 overlay +
 * bg-reader-overlay 背景 + 背景 click で close) を踏襲する。
 *
 * - Esc キーで close (Req 8.3 アクセシビリティ)
 * - モバイル時は全画面、デスクトップ時は centered dialog (max-w + mt で調整)
 * - aria-modal=true + role=dialog
 * - 初期フォーカスは PalimpsestHeader の閉じるボタンへ (PalimpsestView 内部で自然移動)
 */
export function PalimpsestModal({
  open,
  onClose,
  scenarioId,
  currentWorldlineId,
  onForkSuccess,
}: PalimpsestModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Escape close + initial focus + Tab focus trap を hook に委譲 (= SettingsPanel と同 pattern)。
  useDialogKeyboard({ containerRef: dialogRef, open, onClose });

  if (!open) return null;

  return (
    <div
      data-testid="palimpsest-modal-backdrop"
      className="fixed inset-0 z-50 flex items-start justify-center bg-reader-overlay overflow-y-auto p-4 sm:p-6"
      onClick={(e) => {
        // backdrop (自分自身) click のみ close。子要素 click は伝播で来るので currentTarget 判定。
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        data-testid="palimpsest-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="palimpsest 世界線ビュー"
        className="relative w-full max-w-4xl bg-reader-page rounded-lg shadow-xl mt-4 sm:mt-8 max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="overflow-auto flex-1 p-3">
          <PalimpsestView
            scenarioId={scenarioId}
            currentWorldlineId={currentWorldlineId}
            onClose={onClose}
            {...(onForkSuccess !== undefined ? { onForkSuccess } : {})}
          />
        </div>
      </div>
    </div>
  );
}
